import React from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { createLoadOptions, selectStyles } from "../../utilities";

// Reusable, config-driven filter modal. Reproduces the existing admin filter-modal
// markup so the look is unchanged, but every surface drives it from a config array
// (see src/config/filterConfigs.js). It is purely presentational/controlled: it reads
// filters[key] and calls onChange({...}); value normalization (.value extraction) +
// any field remaps live in the surface's buildFilterPayload, not here.
//
// Field config: { key, label, type, ... }
//   type 'text'|'number'                  -> <input>
//   type 'date'                           -> single <input type=date>
//   type 'dateRange'                      -> two date inputs (startKey/endKey)
//   type 'select'                         -> native <select> (+ options [{value,label}])
//   type 'reactSelect'                    -> react-select (static options, clearable)
//   type 'asyncSelect'                    -> AsyncSelect over master data:
//        model (string, e.g. 'mst-location'), masterDataKey, optionLabel (string key OR fn),
//        religionIdFrom (reads filters[x]?.value for createLoadOptions), disabledWhenEmpty,
//        resetOnChange ([keys cleared when this changes]), pageSize, defaultLimit

const inputCls =
  "w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400";
const nativeSelectCls = inputCls + " bg-white";

const defLabel = (field, e) =>
  typeof field.optionLabel === "function" ? field.optionLabel(e) : e[field.optionLabel];

export default function FilterPanel({
  isOpen,
  onClose,
  title = "Filters",
  config = [],
  filters = {},
  onChange,
  onApply,
  onReset,
  masterData = {},
  columns = 3,
}) {
  if (!isOpen) return null;

  const set = (key, value, clearKeys = []) => {
    const next = { ...filters, [key]: value };
    clearKeys.forEach((k) => {
      next[k] = "";
    });
    onChange(next);
  };

  const renderField = (field) => {
    const v = filters[field.key];
    switch (field.type) {
      case "text":
      case "number":
        return (
          <input
            type={field.type === "number" ? "number" : "text"}
            value={v || ""}
            placeholder={field.placeholder || ""}
            onChange={(e) => set(field.key, e.target.value)}
            className={inputCls}
          />
        );

      case "date":
        return (
          <input
            type="date"
            value={v || ""}
            onChange={(e) => set(field.key, e.target.value)}
            className={inputCls}
          />
        );

      case "dateRange":
        return (
          <div className="flex gap-2">
            <input
              type="date"
              value={filters[field.startKey] || ""}
              onChange={(e) => set(field.startKey, e.target.value)}
              className={inputCls}
            />
            <input
              type="date"
              value={filters[field.endKey] || ""}
              onChange={(e) => set(field.endKey, e.target.value)}
              className={inputCls}
            />
          </div>
        );

      case "select": // native
        return (
          <select
            value={v || ""}
            onChange={(e) => set(field.key, e.target.value)}
            className={nativeSelectCls}
          >
            <option value="">{field.allLabel || "All"}</option>
            {(field.options || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        );

      case "reactSelect":
        return (
          <Select
            isClearable
            styles={selectStyles}
            placeholder={field.placeholder || "Select..."}
            options={field.options || []}
            value={
              v && typeof v === "object"
                ? v
                : v
                ? (field.options || []).find((o) => o.value === v) || { value: v, label: v }
                : null
            }
            onChange={(sel) => set(field.key, sel || "")}
          />
        );

      case "asyncSelect": {
        const religionId = field.religionIdFrom
          ? filters[field.religionIdFrom]?.value || ""
          : "";
        const disabled = field.disabledWhenEmpty ? !filters[field.disabledWhenEmpty] : false;
        const defaults = (masterData?.[field.masterDataKey] || [])
          .slice(0, field.defaultLimit || 5)
          .map((e) => ({ value: e._id, label: defLabel(field, e) }));
        return (
          <AsyncSelect
            cacheOptions
            isClearable
            styles={selectStyles}
            placeholder={field.placeholder || "Select..."}
            isDisabled={disabled}
            loadOptions={createLoadOptions(field.model, field.pageSize || 50, religionId)}
            defaultOptions={defaults}
            value={v || null}
            onChange={(sel) => set(field.key, sel || "", field.resetOnChange || [])}
          />
        );
      }

      default:
        return null;
    }
  };

  const colCls =
    columns === 2
      ? "grid grid-cols-1 sm:grid-cols-2 gap-4"
      : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4";

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className={`px-6 py-4 overflow-y-auto ${colCls}`}>
          {config.map((field) => (
            <div key={field.key || field.startKey}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{field.label}</label>
              {renderField(field)}
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0">
          <button
            onClick={() => {
              onReset();
              onClose();
            }}
            className="flex-1 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            Reset
          </button>
          <button
            onClick={() => {
              onApply();
              onClose();
            }}
            className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
